import { Link } from "react-router-dom";
import { ArrowRight, Compass, Link2 } from "lucide-react";
import Globe3D from "@/components/three/Globe3D";
import DashboardMockup from "@/components/mockups/DashboardMockup";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-12 md:pt-20 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Backgrounds */}
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#FB923C] mix-blend-screen blur-[140px] opacity-30 pointer-events-none" />
      <div className="absolute top-40 -left-32 w-[400px] h-[400px] rounded-full bg-[#F97316] mix-blend-screen blur-[140px] opacity-20 pointer-events-none" />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 items-center">
          {/* Left */}
          <div className="reveal">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] pulse-dot" />
              <span className="text-xs text-[#94A3B8] font-medium">
                AI-powered locate ticket operations
              </span>
            </div>

            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              The AI Operating System for{" "}
              <span className="relative inline-block">
                <span className="text-[#F97316]">Locate Ticket</span>
                <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8" preserveAspectRatio="none">
                  <path d="M0 4 Q 50 0 100 4 T 200 4" stroke="#F97316" strokeWidth="2" fill="none" opacity="0.6" />
                </svg>
              </span>{" "}
              Management.
            </h1>

            <p className="mt-6 text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl">
              LocateOS gives underground utility teams one customizable platform to manage tickets,
              dispatch locators, capture field proof, monitor compliance, and turn daily production
              into real-time operational intelligence.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/demo"
                data-testid="hero-request-demo-btn"
                className="group inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#ea580c] text-white font-semibold px-6 py-3.5 rounded-lg transition-all shadow-[0_8px_32px_rgba(249,115,22,0.4)] active:scale-95"
              >
                Request a Demo
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/product"
                data-testid="hero-explore-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg transition-colors bg-white/[0.02]"
              >
                <Compass size={16} />
                Explore Platform
              </Link>
            </div>

            {/* tiny metrics */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "12+", l: "Markets supported" },
                { v: "98%", l: "Field uptime" },
                { v: "40%", l: "Less admin time" },
              ].map((m) => (
                <div key={m.l}>
                  <div className="font-mono font-bold text-2xl text-white">{m.v}</div>
                  <div className="text-xs text-[#94A3B8]">{m.l}</div>
                </div>
              ))}
            </div>

            {/* 811 compatibility trust bar */}
            <div
              data-testid="hero-811-trust"
              className="mt-8 inline-flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-full border border-[#22C55E]/25 bg-[#22C55E]/[0.06] px-4 py-2.5 text-xs sm:text-sm text-[#94A3B8]"
            >
              <Link2 size={14} className="text-[#22C55E] shrink-0" />
              <span className="text-white font-medium">Fully compatible with 811 one-call systems</span>
              <span className="text-white/20 hidden sm:inline">·</span>
              <span>Supports all state one-call centers</span>
              <span className="text-white/20 hidden sm:inline">·</span>
              <span>Direct ticket feed integration</span>
              <span className="text-white/20 hidden sm:inline">·</span>
              <span>Utilisphere compatible</span>
            </div>
          </div>

          {/* Right - 3D globe */}
          <div className="reveal relative h-[360px] md:h-[460px] lg:h-[520px] animate-float-slow">
            <div className="absolute inset-0 rounded-full bg-[#FB923C]/20 blur-3xl" />
            <Globe3D className="h-full w-full" />
          </div>
        </div>

        {/* Dashboard below */}
        <div className="reveal mt-14 md:mt-20 relative">
          <div className="absolute -inset-x-10 -inset-y-6 bg-gradient-to-tr from-[#FB923C]/30 via-transparent to-[#F97316]/20 blur-3xl rounded-full pointer-events-none" />
          <div className="relative animate-float-slow">
            <DashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
